import { ReactNode } from "react";
import moment from "moment";
import type { SummaryField } from "@components/TransactionSummary";
import { formatValue } from "@utils/format";
import { getTransactionStatusLabelKey, resolveTransactionStatus } from "@utils/transactionStatus";
import type { AuditTransactionDetails } from "../../types/audit";

export const EMPTY_VALUE = "-";
export const TIMELINE_CIRCLE_SIZE = 32;
export const TIMELINE_CONNECTOR_WIDTH = 2;

export const STATUS_BADGE_CLASS: Record<string, string> = {
  previsto: "badge bg-lightest border border-color-lighter",
  realizado: "badge bg-white text-color-success border border-color-success",
  vencido: "badge bg-white text-color-danger border border-color-danger",
};

export const formatDate = (value?: string | null): string => {
  const parsed = moment(value);
  return value && parsed.isValid() ? parsed.format("DD/MM/YYYY") : EMPTY_VALUE;
};

export const formatText = (value?: string | null): string => (value ? String(value) : EMPTY_VALUE);

export const formatAmount = (value?: number | null): string =>
  value === null || value === undefined ? EMPTY_VALUE : formatValue(value);

export const formatTaxes = (amount?: number | null, percentage?: number | null): string => {
  const hasAmount = amount !== null && amount !== undefined;
  const hasPercentage = percentage !== null && percentage !== undefined;

  if (hasAmount && hasPercentage) return `${formatValue(amount)} (${percentage}%)`;
  if (hasAmount) return formatValue(amount);
  if (hasPercentage) return `${percentage}%`;

  return EMPTY_VALUE;
};

type Translate = (id: string) => string;

export const buildStatusSummaryColumn = (
  t: Translate,
  details: AuditTransactionDetails | null,
  module?: string | null
): SummaryField[] => {
  const statusKind = resolveTransactionStatus(details?.status, null, details?.createdAt);

  return [
    {
      icon: "rioglyph-ok-sign",
      label: t("expense.audit.label.status"),
      value: details ? (
        <span className={STATUS_BADGE_CLASS[statusKind]}>
          {t(getTransactionStatusLabelKey(module, details.status))}
        </span>
      ) : (
        EMPTY_VALUE
      ),
    },
    {
      icon: "rioglyph-calendar",
      label: t("expense.audit.label.createdAt"),
      value: formatDate(details?.createdAt),
    },
  ];
};

export const buildAmountSummaryColumn = (t: Translate, details: AuditTransactionDetails | null): SummaryField[] => [
  { symbol: "R$", label: t("expense.audit.label.amount"), value: formatAmount(details?.amount) },
  {
    icon: "rioglyph-tag",
    label: t("expense.audit.label.serviceTaxes"),
    value: formatTaxes(details?.taxAmount, details?.taxPercentage),
  },
];

export const buildPartySummaryColumns = (t: Translate, details: AuditTransactionDetails | null): SummaryField[][] => [
  [
    { icon: "rioglyph-businessman", label: t("expense.audit.label.client"), value: formatText(details?.clientName) },
    { icon: "rioglyph-route-option", label: t("expense.audit.label.trip"), value: formatText(details?.tripCode) },
  ],
  [
    { icon: "rioglyph-truck", label: t("expense.audit.label.vehicle"), value: formatText(details?.vehiclePlate) },
    { icon: "rioglyph-driver", label: t("expense.audit.label.driver"), value: formatText(details?.driverName) },
  ],
];

interface TimelineMarkerProps {
  isLast: boolean;
  glyph: ReactNode;
}

export const TimelineMarker = ({ isLast, glyph }: Readonly<TimelineMarkerProps>) => (
  <div
    className="display-flex flex-column align-items-center align-self-stretch"
    style={{ flex: "0 0 auto", width: TIMELINE_CIRCLE_SIZE }}
  >
    <div
      className="bg-lightest rounded-circle display-flex align-items-center justify-content-center"
      style={{ width: TIMELINE_CIRCLE_SIZE, height: TIMELINE_CIRCLE_SIZE, flex: "0 0 auto" }}
    >
      {glyph}
    </div>
    {!isLast && (
      <div
        className="border-color-lighter"
        style={{
          width: 0,
          flex: "1 1 auto",
          minHeight: 20,
          borderLeftWidth: TIMELINE_CONNECTOR_WIDTH,
          borderLeftStyle: "dashed",
        }}
      />
    )}
  </div>
);

interface TimelineRowProps {
  isLast: boolean;
  glyph: ReactNode;
  children: ReactNode;
}

export const TimelineRow = ({ isLast, glyph, children }: Readonly<TimelineRowProps>) => (
  <div className="display-flex align-items-start gap-15">
    <TimelineMarker isLast={isLast} glyph={glyph} />

    <div
      className={`flex-1-1 border border-color-light rounded-small display-flex flex-wrap ${
        isLast ? "" : "margin-bottom-15"
      }`}
      style={{ minWidth: 0 }}
    >
      {children}
    </div>
  </div>
);
